import { useQuery } from "@tanstack/react-query";
import { Boxes } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { CanonicalDetailPanel } from "@/components/canonical/CanonicalDetailPanel";
import { CanonicalListPanel } from "@/components/canonical/CanonicalListPanel";
import { NewCanonicalDialog } from "@/components/canonical/NewCanonicalDialog";
import { TagFilter } from "@/components/tags";
import { Alert } from "@/components/ui/alert";
import { EmptyState, PageContainer, PageHeader } from "@/components/ui/page";
import { canonicalApi } from "@/lib/api-canonical";
import { usePermissions } from "@/lib/permissions";

interface Selection {
  canonical_type: string;
  version: number;
}

type DialogMode =
  | { kind: "new-type" }
  | { kind: "new-version"; baseYaml: string; nextVersion: number; canonical_type: string };

export function CanonicalPage() {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const canWrite = can("canonical", "write");
  const canDelete = can("canonical", "delete");
  const [selected, setSelected] = React.useState<Selection | null>(null);
  const [tagFilter, setTagFilter] = React.useState<string[]>([]);
  const [dialog, setDialog] = React.useState<{ open: boolean; mode: DialogMode }>({
    open: false,
    mode: { kind: "new-type" },
  });

  // CanonicalPage is master-detail: the left panel needs the FULL list
  // of canonical schemas to show the version tree, so we ask for a large
  // page (100 is well above any realistic canonical count) and treat the
  // response as the complete set. If a deployment ever exceeds 100, the
  // list panel would need its own pagination — switch to a virtual list
  // before pagination becomes necessary.
  const listQuery = useQuery({
    queryKey: ["canonical", "all", tagFilter],
    queryFn: () =>
      canonicalApi.list({
        page_size: 100,
        tag: tagFilter.length > 0 ? tagFilter : undefined,
      }),
    select: (page) => page.items,
  });

  React.useEffect(() => {
    if (selected || !listQuery.data || listQuery.data.length === 0) return;
    const first = listQuery.data[0];
    setSelected({ canonical_type: first.canonical_type, version: first.version });
  }, [listQuery.data, selected]);

  React.useEffect(() => {
    if (!selected || !listQuery.data) return;
    const exists = listQuery.data.some(
      (it) => it.canonical_type === selected.canonical_type && it.version === selected.version,
    );
    if (!exists) {
      const first = listQuery.data[0];
      setSelected(
        first ? { canonical_type: first.canonical_type, version: first.version } : null,
      );
    }
  }, [listQuery.data, selected]);

  const detailQuery = useQuery({
    queryKey: ["canonical", selected?.canonical_type, selected?.version],
    queryFn: () => canonicalApi.get(selected!.canonical_type, selected!.version),
    enabled: !!selected,
  });

  // Previous active version for diff
  const previousActive = React.useMemo(() => {
    if (!detailQuery.data || !listQuery.data) return null;
    const sameType = listQuery.data.filter(
      (it) =>
        it.canonical_type === detailQuery.data!.canonical_type &&
        it.version < detailQuery.data!.version &&
        (it.status === "active" || it.status === "deprecated"),
    );
    sameType.sort((a, b) => b.version - a.version);
    return sameType[0] ?? null;
  }, [detailQuery.data, listQuery.data]);

  const previousActiveDetailQuery = useQuery({
    queryKey: ["canonical", previousActive?.canonical_type, previousActive?.version],
    queryFn: () =>
      canonicalApi.get(previousActive!.canonical_type, previousActive!.version),
    enabled: !!previousActive,
  });

  // Highest version known for the currently selected canonical_type
  const highestVersion = React.useMemo(() => {
    if (!detailQuery.data || !listQuery.data) return 0;
    return listQuery.data
      .filter((it) => it.canonical_type === detailQuery.data!.canonical_type)
      .reduce((m, it) => Math.max(m, it.version), 0);
  }, [detailQuery.data, listQuery.data]);

  return (
    <PageContainer>
      <PageHeader title={t("canonical.title")} description={t("canonical.subtitle")} />
      {/* Plain soft hairline (no gradient), full width, between the page
          header and the master-detail. */}
      <div className="mb-1 h-px shrink-0 bg-border/60" />
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <CanonicalListPanel
          items={listQuery.data ?? []}
          selected={selected}
          onSelect={(t, v) => setSelected({ canonical_type: t, version: v })}
          onNew={() => setDialog({ open: true, mode: { kind: "new-type" } })}
          canEdit={canWrite}
          tagFilterSlot={
            <TagFilter
              value={tagFilter}
              onChange={setTagFilter}
              scope="canonicals"
              className="w-full"
            />
          }
        />

        <div className="flex flex-1 flex-col overflow-hidden">
          {listQuery.isLoading ? (
            <div className="p-6 text-sm text-muted-foreground">Loading…</div>
          ) : detailQuery.data ? (
            <CanonicalDetailPanel
              detail={detailQuery.data}
              previousActive={previousActiveDetailQuery.data ?? null}
              canEdit={canWrite}
              canDelete={canDelete}
              highestVersion={highestVersion}
              onDeleted={() => setSelected(null)}
              onNewVersion={(baseYaml, nextVersion, canonical_type) =>
                setDialog({
                  open: true,
                  mode: { kind: "new-version", baseYaml, nextVersion, canonical_type },
                })
              }
            />
          ) : (
            <div className="grid h-full place-items-center p-6">
              {!canWrite && listQuery.data?.length === 0 && (
                <Alert tone="warning" title="No canonical models defined">
                  Only admins can create canonical models.
                </Alert>
              )}
              {canWrite && listQuery.data?.length === 0 && (
                <EmptyState
                  icon={Boxes}
                  title="No canonical models"
                  description="Bootstrap will seed mission.read.v1 and mission.execution.v1 on first run. You can also create one now."
                />
              )}
            </div>
          )}
        </div>
      </div>

      <NewCanonicalDialog
        open={dialog.open}
        mode={dialog.mode}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        onCreated={(t, v) => setSelected({ canonical_type: t, version: v })}
      />
    </PageContainer>
  );
}
